import {
  PERMISSION_LEVEL_LABELS,
  PERMISSION_LEVELS,
  PERMISSION_MODULE_LABELS,
  PERMISSION_MODULES,
  type PermissionLevelValue,
  type PermissionModule,
} from '@crm/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  Input,
  Label,
  Select,
  Spinner,
  Textarea,
} from '@crm/ui';
import { ArrowLeft, Lock, ShieldCheck, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { SECTIONS } from '@/app/layouts/navigation';
import { ConfirmDialog, PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import { PROFILES_ROUTES } from '../constants';
import { useCreateProfile, useDeleteProfile, useProfile, useUpdateProfile } from '../hooks';
import type { PermissionEntry } from '../types';

type Matriz = Record<PermissionModule, PermissionLevelValue>;

const VAZIA = Object.fromEntries(PERMISSION_MODULES.map((modulo) => [modulo, 'NONE'])) as Matriz;

function toMatriz(permissions: PermissionEntry[]): Matriz {
  const matriz = { ...VAZIA };
  for (const entrada of permissions) matriz[entrada.module] = entrada.level;
  return matriz;
}

/** So o que tem nivel viaja: "sem acesso" e a ausencia da linha. */
function toEntries(matriz: Matriz): PermissionEntry[] {
  return PERMISSION_MODULES.filter((modulo) => matriz[modulo] !== 'NONE').map((modulo) => ({
    module: modulo,
    level: matriz[modulo],
  }));
}

export function ProfileFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  useDocumentTitle(isEdit ? 'Editar perfil' : 'Novo perfil');

  const navigate = useNavigate();
  const profile = useProfile(id);
  const deleteProfile = useDeleteProfile();
  const [pendingRemoval, setPendingRemoval] = useState(false);
  const createProfile = useCreateProfile();
  const updateProfile = useUpdateProfile();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [homeRoute, setHomeRoute] = useState('');
  const [matriz, setMatriz] = useState<Matriz>(VAZIA);

  // biome-ignore lint/correctness/useExhaustiveDependencies: preenche uma vez, quando o perfil chega
  useEffect(() => {
    if (!profile.data) return;

    setName(profile.data.name);
    setDescription(profile.data.description ?? '');
    setHomeRoute(profile.data.homeRoute ?? '');
    setMatriz(toMatriz(profile.data.permissions));
  }, [profile.data]);

  const isSystem = profile.data?.isSystem ?? false;
  const salvando = createProfile.isPending || updateProfile.isPending;
  const erro = createProfile.error ?? updateProfile.error;

  function salvar() {
    const permissions = toEntries(matriz);

    if (isEdit && id) {
      // Nome de perfil do sistema nao vai no corpo: a API recusaria.
      updateProfile.mutate(
        {
          id,
          input: isSystem
            ? { permissions, homeRoute: homeRoute || null }
            : { name, description, homeRoute: homeRoute || null, permissions },
        },
        { onSuccess: () => navigate(PROFILES_ROUTES.LIST) },
      );
      return;
    }

    createProfile.mutate(
      { name, description, homeRoute: homeRoute || null, permissions },
      { onSuccess: () => navigate(PROFILES_ROUTES.LIST) },
    );
  }

  if (isEdit && profile.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ShieldCheck />}
        title={isEdit ? `Perfil ${profile.data?.name ?? ''}` : 'Novo perfil'}
        description="O acesso do usuario e a soma dos perfis dele - o maior nivel prevalece."
        actions={
          <>
            <Button variant="outline" asChild>
              <Link to={PROFILES_ROUTES.LIST}>
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </Link>
            </Button>

            {/* Excluir mora aqui, no perfil: na lista do celular era mais um
                botao pequeno repetido linha a linha. Perfil do sistema existe
                em toda empresa e nao se apaga. */}
            {isEdit && !isSystem ? (
              <Button
                variant="ghost"
                disabled={deleteProfile.isPending}
                onClick={() => setPendingRemoval(true)}
              >
                <Trash2 className="h-4 w-4" />
                Excluir
              </Button>
            ) : null}
          </>
        }
      />

      <Card variant="flush">
        <CardHeader>
          <CardTitle className="text-base">Identificacao</CardTitle>
          <CardDescription>
            {isSystem
              ? 'Perfil do sistema: o nome e fixo, mas as permissoes voce ajusta.'
              : 'Um nome que descreva a funcao de quem recebe este perfil.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="profile-name">Nome</Label>
            <Input
              id="profile-name"
              value={name}
              disabled={isSystem}
              onChange={(event) => setName(event.target.value)}
              placeholder="Ex.: Recepcao"
            />
          </div>

          {/* Pagina inicial: o perfil decide onde o usuario cai ao entrar.
              As opcoes sao as proprias telas do menu - nada de rota digitada
              a mao, que quebraria no primeiro caminho renomeado. */}
          <div className="space-y-2">
            <Label htmlFor="profile-home">Pagina inicial</Label>
            <Select
              id="profile-home"
              value={homeRoute}
              onChange={(event) => setHomeRoute(event.target.value)}
            >
              <option value="">Painel</option>
              {SECTIONS.flatMap((section) =>
                section.items.map((item) => (
                  <option key={item.to} value={item.to}>
                    {section.title} - {item.label}
                  </option>
                )),
              )}
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile-description">Descricao</Label>
            <Textarea
              id="profile-description"
              rows={1}
              value={description}
              disabled={isSystem}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="O que este perfil faz no dia a dia"
            />
          </div>
        </CardContent>
      </Card>

      <Card variant="flush">
        <CardHeader>
          <CardTitle className="text-base">Permissoes</CardTitle>
          <CardDescription>
            A lista de modulos e de niveis e do sistema - o que se configura aqui e o nivel de cada
            modulo neste perfil.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {PERMISSION_MODULES.map((modulo) => (
            <div
              key={modulo}
              className="flex flex-col gap-2 border-b border-border py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between"
            >
              <p className="font-medium">{PERMISSION_MODULE_LABELS[modulo]}</p>

              {/* Tres estados em botoes, nao em select: o nivel de cada modulo
                  se le de relance, sem abrir nada. */}
              <div className="flex gap-1 rounded-lg bg-muted p-1">
                {PERMISSION_LEVELS.map((nivel) => (
                  <button
                    key={nivel}
                    type="button"
                    onClick={() => setMatriz((atual) => ({ ...atual, [modulo]: nivel }))}
                    className={cn(
                      'h-9 flex-1 rounded-md px-3 text-sm transition-colors sm:flex-none',
                      matriz[modulo] === nivel
                        ? 'bg-background font-medium text-foreground shadow-sm'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {PERMISSION_LEVEL_LABELS[nivel]}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {erro ? <p className="text-sm text-destructive">{toErrorMessage(erro)}</p> : null}

      <div className="flex items-center gap-3">
        <Button onClick={salvar} disabled={salvando || (!isSystem && name.trim().length < 2)}>
          {salvando ? <Spinner /> : null}
          {isEdit ? 'Salvar perfil' : 'Criar perfil'}
        </Button>

        {isSystem ? (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Lock className="h-3.5 w-3.5" />
            Perfil do sistema: nao pode ser renomeado nem excluido.
          </p>
        ) : null}
      </div>

      <ConfirmDialog
        open={pendingRemoval}
        title="Excluir perfil"
        message="O perfil sai do sistema. Usuarios que dependem so dele ficam sem acesso."
        confirmLabel="Excluir"
        loading={deleteProfile.isPending}
        onClose={() => setPendingRemoval(false)}
        onConfirm={() => {
          if (id) deleteProfile.mutate(id, { onSuccess: () => navigate(PROFILES_ROUTES.LIST) });
          setPendingRemoval(false);
        }}
      />
    </div>
  );
}
